import { useQuery, useLazyQuery } from '@apollo/client';

import {
    GET_SURVEYS,
    GET_SURVEY,
    GET_SURVEY_COUNTS,
    GET_SURVEY_PRODUCT_LISTS,
    GET_SURVEY_PRODUCTS_BY_IMPORT_LOG,
    GET_PROOF_OF_SURVEY_COMPETITOR_PRICING,
} from '../../apollo/queries/survey';
import { GET_ACCESSIBLE_ADVERTISING_ZONES } from '../../apollo/queries/advertisingZone';
import { GET_ACCESSIBLE_SURVEY_STORES } from '../../apollo/queries/store';
import { GET_DROPDOWN_USERS } from '../../apollo/queries/user';
import { SurveyFilterInput } from '../../codegen/graphql';
import { SurveyType } from '../../constants/surveyTypes';

export function useGetSurveys(filter: SurveyFilterInput) {
    return useQuery(GET_SURVEYS, {
        variables: { filter },
    });
}

export function useGetSurvey(id: string) {
    return useQuery(GET_SURVEY, {
        variables: { id },
        skip: !id,
    });
}

export function useGetSurveyCounts(filter?: {
    advertisingZoneId?: string;
    createdAtStart?: Date;
    createdAtEnd?: Date;
    surveyType?: SurveyType;
}) {
    return useQuery(GET_SURVEY_COUNTS, {
        variables: { filter },
    });
}

export function useGetSurveyProductListsLazy() {
    return useLazyQuery(GET_SURVEY_PRODUCT_LISTS, {
        fetchPolicy: 'network-only',
    });
}

export function useGetSurveyProductsByImportLogLazy() {
    return useLazyQuery(GET_SURVEY_PRODUCTS_BY_IMPORT_LOG, {
        fetchPolicy: 'network-only',
    });
}

export function useGetAccessibleAdvertisingZones(skip = false) {
    return useQuery(GET_ACCESSIBLE_ADVERTISING_ZONES, {
        skip,
    });
}

export function useGetAccessibleSurveyStores(skip = false) {
    return useQuery(GET_ACCESSIBLE_SURVEY_STORES, {
        skip,
    });
}

export function useGetDivisionalManagers(skip = false) {
    return useQuery(GET_DROPDOWN_USERS, {
        skip,
        variables: {
            roleFilter: 'Divisional Manager',
            limit: 1000,
        },
    });
}

interface GetStoreManagersParams {
    skip?: boolean;
    nameSearch?: string;
    advertisingZoneId?: string;
    storeId?: string;
}

export function useGetStoreManagers(params: GetStoreManagersParams = {}) {
    const { skip = false, nameSearch, advertisingZoneId, storeId } = params;

    return useQuery(GET_DROPDOWN_USERS, {
        skip: skip || (!nameSearch && !advertisingZoneId && !storeId),
        variables: {
            roleFilter: 'Store Manager',
            advertisingZoneId,
            storeId,
            nameSearch,
            limit: 1000,
        },
    });
}

export const useGetProofOfSurveyCompetitorPricing = (
    priceSurveyCompetitorId: string,
    skip = false,
) => {
    return useQuery(GET_PROOF_OF_SURVEY_COMPETITOR_PRICING, {
        variables: { priceSurveyCompetitorId },
        skip: skip || !priceSurveyCompetitorId,
        fetchPolicy: 'network-only',
    });
};
